/**
 * ⚠️ HAND-WRITTEN FILE - DO NOT DELETE! ⚠️
 *
 * Privacy Policy page — required for Google OAuth branding verification.
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { Box, Container, Typography, Divider, Stack } from '@mui/material';
import { useThemeContext } from '@/contexts/ThemeContext';
import { createPageMeta } from '@/lib/meta';

const LAST_UPDATED = 'September 10, 2026';

const CONTACT_EMAIL: string | null = null;

export const Route = createFileRoute('/privacy-policy')({
  component: PrivacyPolicyPage,
  head: () => createPageMeta({
    title: 'Privacy Policy - Christian Battaglia',
    description: 'Privacy policy for cdbattaglia.com - how we collect, use, and protect your information.',
    path: '/privacy-policy',
    image: '/og/privacy-policy.png',
  }),
});

function ContactInfo() {
  if (CONTACT_EMAIL) {
    return <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>;
  }
  return <em>Contact email coming soon</em>;
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant="h5" component="h2" fontWeight={700} sx={{ mt: 5, mb: 2 }}>
      {children}
    </Typography>
  );
}

function PrivacyPolicyPage() {
  const { mode } = useThemeContext();
  const linkColor = mode === 'dark' ? '#60a5fa' : '#1e3a8a';

  return (
    <Box sx={{ minHeight: 'calc(100vh - 70px)', bgcolor: 'background.default' }}>
      <Container maxWidth="md" sx={{ py: { xs: 4, md: 8 } }}>
        <Typography variant="h3" component="h1" fontWeight={800} sx={{ mb: 1 }}>
          Privacy Policy
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Last updated: {LAST_UPDATED}
        </Typography>
        <Divider sx={{ mb: 4 }} />

        <Stack spacing={0} sx={{
          '& p': { mb: 2, lineHeight: 1.8 },
          '& ul': { pl: 3, mb: 2 },
          '& li': { mb: 1, lineHeight: 1.7 },
          '& a': { color: linkColor, textDecoration: 'underline' },
        }}>
          {/* 1. Introduction */}
          <SectionHeading>1. Introduction</SectionHeading>
          <Typography>
            This website, <strong>cdbattaglia.com</strong>, is a personal portfolio and blog operated by
            Christian Battaglia. This Privacy Policy explains what information we collect, how we use it,
            and your choices regarding that information.
          </Typography>
          <Typography>
            By using this site, you agree to the collection and use of information as described here.
            This policy should be read alongside our{' '}
            <Link to="/terms-of-service" style={{ color: linkColor }}>Terms of Service</Link>.
          </Typography>

          {/* 2. Information We Collect */}
          <SectionHeading>2. Information We Collect</SectionHeading>

          <Typography variant="h6" component="h3" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
            Analytics (PostHog)
          </Typography>
          <Typography>
            We use <a href="https://posthog.com/privacy" target="_blank" rel="noopener noreferrer">PostHog</a>{' '}
            to understand how visitors interact with the site. PostHog automatically collects:
          </Typography>
          <ul>
            <li><Typography component="span">Page views, navigation, referrers, and UTM parameters</Typography></li>
            <li><Typography component="span">Clicks, copy/paste, scroll depth, time on page, print, and file drops</Typography></li>
            <li><Typography component="span">Form submissions (field values are not collected)</Typography></li>
            <li><Typography component="span">Session recordings (form inputs are masked)</Typography></li>
            <li><Typography component="span">Heatmaps, rage clicks, and dead clicks</Typography></li>
            <li><Typography component="span">Web vitals and JavaScript exceptions</Typography></li>
            <li><Typography component="span">Device type, browser, operating system, and approximate location (country/region)</Typography></li>
          </ul>
          <Typography>
            We also capture custom events for feature usage — search, filters, theme, music analyzer,
            3D scene interaction, command palette, and content engagement. PostHog is hosted on
            PostHog Cloud in the United States. Analytics requests are proxied first-party through
            this site so they do not go directly to PostHog domains.
          </Typography>

          <Typography variant="h6" component="h3" fontWeight={600} sx={{ mt: 3, mb: 1 }}>
            Spotify Integration
          </Typography>
          <Typography>
            The Music Analyzer page allows you to optionally connect your Spotify account. When you do,
            we receive your Spotify email address and display name via the{' '}
            <a href="https://developer.spotify.com/documentation/web-api" target="_blank" rel="noopener noreferrer">
              Spotify Web API
            </a>.
            Your access and refresh tokens are stored in your browser's localStorage and are never sent to our servers.
            We use your email to identify you in our analytics for a better experience.
          </Typography>

          <Typography variant="h6" component="h3" fontWeight={600} sx={{ mt: 3, mb: 1 }}>
            Google Sign-In
          </Typography>
          <Typography>
            Certain features (such as the contact page) use Google Sign-In. When you sign in with Google,
            we receive your email address and profile name. This information is used solely to authenticate
            you and enable access to gated features.
          </Typography>

          <Typography variant="h6" component="h3" fontWeight={600} sx={{ mt: 3, mb: 1 }}>
            Local Storage
          </Typography>
          <Typography>
            We store preferences and state in your browser's localStorage, including:
          </Typography>
          <ul>
            <li><Typography component="span">Theme preference (light/dark mode)</Typography></li>
            <li><Typography component="span">Retro mode unlock state</Typography></li>
            <li><Typography component="span">Achievement progress</Typography></li>
            <li><Typography component="span">Spotify authentication tokens</Typography></li>
          </ul>
          <Typography>
            This data never leaves your browser and is not transmitted to any server.
          </Typography>

          {/* 3. How We Use Information */}
          <SectionHeading>3. How We Use Information</SectionHeading>
          <Typography>We use the information we collect to:</Typography>
          <ul>
            <li><Typography component="span">Understand which content resonates with visitors</Typography></li>
            <li><Typography component="span">Improve site performance and user experience</Typography></li>
            <li><Typography component="span">Enable Spotify playback and music analysis features</Typography></li>
            <li><Typography component="span">Authenticate users for gated features (Google Sign-In)</Typography></li>
          </ul>
          <Typography>
            We do <strong>not</strong> sell your data. We do <strong>not</strong> run advertising.
            We do <strong>not</strong> share your personal information with third parties for marketing purposes.
          </Typography>

          {/* 4. Third-Party Services */}
          <SectionHeading>4. Third-Party Services</SectionHeading>
          <Typography>This site integrates with the following third-party services:</Typography>
          <ul>
            <li>
              <Typography component="span">
                <strong>PostHog</strong> — product analytics.{' '}
                <a href="https://posthog.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
              </Typography>
            </li>
            <li>
              <Typography component="span">
                <strong>Spotify</strong> — music playback and search.{' '}
                <a href="https://www.spotify.com/legal/privacy-policy/" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
              </Typography>
            </li>
            <li>
              <Typography component="span">
                <strong>Google</strong> — authentication.{' '}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
              </Typography>
            </li>
          </ul>

          {/* 5. Cookies and Tracking */}
          <SectionHeading>5. Cookies and Tracking</SectionHeading>
          <Typography>
            PostHog sets first-party cookies for session tracking, user identification, and session
            replay. No third-party advertising or tracking cookies are used on this site. Form
            field values in session recordings are masked. Spotify authentication uses localStorage
            rather than cookies.
          </Typography>

          {/* 6. Data Retention */}
          <SectionHeading>6. Data Retention</SectionHeading>
          <Typography>
            Analytics data is retained by PostHog according to their{' '}
            <a href="https://posthog.com/docs/privacy" target="_blank" rel="noopener noreferrer">
              default retention policy
            </a>.
            Data stored in your browser's localStorage persists until you clear it manually or clear
            your browser data.
          </Typography>

          {/* 7. Your Rights */}
          <SectionHeading>7. Your Rights</SectionHeading>
          <Typography>You have the right to:</Typography>
          <ul>
            <li><Typography component="span">Clear all localStorage data at any time through your browser settings</Typography></li>
            <li><Typography component="span">Disconnect your Spotify account by clicking "Disconnect" on the Music page</Typography></li>
            <li><Typography component="span">Request deletion of your analytics data by contacting us</Typography></li>
            <li><Typography component="span">Use browser privacy tools or extensions to block analytics</Typography></li>
          </ul>
          <Typography>
            To request data deletion or ask questions about your data, contact us
            at: <ContactInfo />.
          </Typography>

          {/* 8. Children's Privacy */}
          <SectionHeading>8. Children's Privacy</SectionHeading>
          <Typography>
            This site is not directed at children under the age of 13. We do not knowingly collect
            personal information from children. If you believe a child has provided us with personal
            information, please contact us and we will delete it.
          </Typography>

          {/* 9. Changes */}
          <SectionHeading>9. Changes to This Policy</SectionHeading>
          <Typography>
            We may update this Privacy Policy from time to time. When we do, we will update the
            "Last updated" date at the top of this page. Continued use of the site after changes
            constitutes acceptance of the updated policy.
          </Typography>

          {/* 10. Contact */}
          <SectionHeading>10. Contact</SectionHeading>
          <Typography>
            If you have any questions about this Privacy Policy, please contact
            us at: <ContactInfo />.
          </Typography>
        </Stack>
      </Container>
    </Box>
  );
}
